import { TFunction } from "i18next";
import MaleIcon from "assets/icons/tts-custom-editor/male.svg";
import FemaleIcon from "assets/icons/tts-custom-editor/female.svg";

import {
  SelectorOptionsWithTooltip,
  TTSLanguage,
  TTSLanguageMapping,
  TTSLanguageType,
  TTSDataInEnv,
  VoiceDescriptionProps,
  priorityMap,
  TTS,
  TTSLanguageIconMapping,
} from "shared/models";
import { GrpcWebFetchTransport } from "@protobuf-ts/grpcweb-transport";
import { RpcOptions } from "@protobuf-ts/runtime-rpc";
import { Empty } from "@generated/google/protobuf/empty";
import { TextToSpeechRequest } from "generated/tts";
import { TextToSpeechClient } from "generated/tts.client";
import { AudioFormat_Encoding } from "@generated/audio_format";
import {
  ListOfVoices,
  ListOfVoices_VoicesPerFlavor,
} from "@generated/list_of_voices";
import {
  EmotionProps,
  getIconForStyle,
} from "shared/models/tts-editor/emotion";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";

// Fetch all available detailed voices using the enumerateVoices RPC.
export async function fetchVoicesExtended(
  envName: string,
  token: string
): Promise<ListOfVoices | undefined> {
  const baseUrl = getTTSUrl(envName);
  const transport = new GrpcWebFetchTransport({ baseUrl });
  const client = new TextToSpeechClient(transport);
  const rpcOptions: RpcOptions = {
    meta: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const call = client.enumerateVoices(Empty, rpcOptions);
    const response = await call.response;

    // Filter out generic male/female voices
    if (response?.flavors) {
      response.flavors = response.flavors.map((flavor) => ({
        ...flavor,
        voices:
          flavor.voices?.filter(
            (voice) => voice.name !== "male" && voice.name !== "female"
          ) || [],
      }));
    }

    return response;
  } catch (error) {
    return undefined;
  }
}

// Fetch the languages for the given deployment
export function getLanguages(
  envData: TTSDataInEnv[],
  deploymentName: string
): TTSLanguage[] {
  return (
    envData[0]?.data.find((item) => item.name === deploymentName)?.languages ||
    []
  );
}

// Fetch the language names as an array of string
export function getLanguageNames(
  languages: TTSLanguage[],
  dynamicMapping?: Record<string, string>
): SelectorOptionsWithTooltip {
  const languageNames: SelectorOptionsWithTooltip = [];
  languages.forEach((item) => {
    const langCode = item.name.toLowerCase() as TTSLanguageType;
    languageNames.push({
      value: item.name,
      name:
        dynamicMapping?.[langCode] || TTSLanguageMapping[langCode] || item.name,
    });
  });
  return languageNames;
}

// Fetch the voices for the given language
export function getVoices(
  languages: TTSLanguage[],
  selectedLanguage: string
): string[] {
  const language = languages.filter((l) => l.name === selectedLanguage);
  return language?.[0]?.voices || [];
}

export function getWrappedInput(input: string): string {
  return `<speak>${input}</speak>`;
}

export function getCategorizedVoices(
  language: string,
  voicesDescription: VoiceDescriptionProps[]
): {
  native: string[];
  nonNative: string[];
} {
  const native: string[] = [];
  const nonNative: string[] = [];

  const selectedPriority = priorityMap[language.toLowerCase()] || [];

  voicesDescription.forEach((voice) => {
    if (voice.nativeLanguage.toLowerCase() === language.toLowerCase()) {
      native.push(voice.voiceName);
    } else {
      nonNative.push(voice.voiceName);
    }
  });

  // Sort nonNative voices based on the priority
  nonNative.sort((a, b) => {
    const aIndex = selectedPriority.indexOf(
      voicesDescription
        .find((voice) => voice.voiceName === a)
        ?.nativeLanguage.toLowerCase() || ""
    );
    const bIndex = selectedPriority.indexOf(
      voicesDescription
        .find((voice) => voice.voiceName === b)
        ?.nativeLanguage.toLowerCase() || ""
    );

    // Voices not found in the priority map pushed to the end
    return (
      (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex)
    );
  });

  return { native, nonNative };
}

// E.g. Returns English if the input is en-US
export function getLanguageLabel(
  languageCode: string,
  languageOptions: SelectorOptionsWithTooltip
): string {
  return languageOptions.filter((lang) => lang.value === languageCode)?.[0]
    ?.name;
}

// Function to find the voice description from the given list of voicesDescription
export function getVoiceDescription(
  voiceName: string,
  voicesDescription: VoiceDescriptionProps[]
): VoiceDescriptionProps | undefined {
  return voicesDescription.find(
    (voiceDesc) => voiceDesc.voiceName === voiceName
  );
}

/**
 * Function to create a unique list of voice descriptions by looping through
 * each language and the native voices assigned to that language.
 */
export function generateVoiceDescriptions(
  listOfVoices: ListOfVoices | undefined
): VoiceDescriptionProps[] {
  const voicesDescription: VoiceDescriptionProps[] = [];

  if (!listOfVoices || !listOfVoices.flavors) {
    return [];
  }

  listOfVoices.flavors.forEach((flavor) => {
    flavor.voices.forEach((voice) => {
      // Check if a voice with the same name already exists to avoid duplicates
      const existingIndex = voicesDescription.findIndex(
        (v) => v.voiceName === voice.name
      );

      if (existingIndex === -1) {
        voicesDescription.push({
          voiceIcon:
            voice.gender.toLowerCase() === "female" ? FemaleIcon : MaleIcon,
          voiceName: voice.name,
          voiceGender: voice.gender,
          nativeLanguage: voice.nativeLanguage,
          accent: voice.accent,
          origin: voice.origin,
          age: voice.age,
          emotion: voice.emotion,
          style: voice.style,
          previewPrompt: voice.previewPrompt,
          freeText: voice.freeText,
        });
      }
      else if (voice.gender !== "unknown") {
        voicesDescription[existingIndex].voiceIcon =
          voice.gender.toLowerCase() === "female" ? FemaleIcon : MaleIcon;
        voicesDescription[existingIndex].voiceGender = voice.gender;
      }
    });
  });

  return voicesDescription;
}

export function generateLanguageOptions(
  t: TFunction<"translation", undefined>,
  languageOptions: SelectorOptionsWithTooltip
) {
  const translatedOptions = languageOptions.map((option) => ({
    value: option.value,
    name: t(`tts.toolbarMenu.languageMenu.${option.value.toLowerCase()}`),
    icon: TTSLanguageIconMapping[option.value.toLowerCase() as TTSLanguageType],
  }));
  return translatedOptions;
}

export function getSampaData(t: TFunction<"translation", undefined>) {
  const xSampaData = [
    {
      label: t("tts.toolbarMenu.pronunciationMenu.diacritics"),
      data: '"%=ː~`\\',
    },
    {
      label: t("tts.toolbarMenu.pronunciationMenu.vowels"),
      data: "aA{6e@E3i1IoO9Q2uUVyY",
    },
    {
      label: t("tts.toolbarMenu.pronunciationMenu.consonants"),
      data: "bcCdDfghHjklmnNpr4RsStTvwxLzZ?",
    },
  ];
  return xSampaData;
}

export function getIpaData(t: TFunction<"translation", undefined>) {
  const ipaData = [
    {
      label: t("tts.toolbarMenu.pronunciationMenu.diacritics"),
      data: "ˈˌ.ː̃",
    },
    {
      label: t("tts.toolbarMenu.pronunciationMenu.vowels"),
      data: "aɑæɐãeəɚɛɜɝiɨɪoɔœɒõøuʊʌyʏ",
    },
    {
      label: t("tts.toolbarMenu.pronunciationMenu.consonants"),
      data: "bcçdðfghɥjklmnɳŋprɾɹʁʀsʃtθvwxʎzʒʔ",
    },
  ];
  return ipaData;
}

/**
 * Function to download the given text as JSON
 * @param text - text to download as JSON
 * @param callback - callback function to trigger with a success or failure indicator
 */
export function generateFileName(
  voiceName: string,
  nativeLanguage: string,
  extension: string,
  sampleRate?: string
): string {
  const date = new Date();
  const formattedDate = date
    .toISOString()
    .split("T")[0]
    .replace(/-/g, "")
    .slice(2); // YYMMDD
  const formattedTime = date
    .toTimeString()
    .split(" ")[0]
    .replace(/:/g, "")
    .slice(0, 4);

  const fileName = `allinga_tts_${voiceName}_${nativeLanguage}_${sampleRate}hz_${formattedDate}_${formattedTime}.${extension}`;
  return fileName;
}

export function downloadAsJSON(
  text: string,
  voiceName: string,
  nativeLanguage: string,
  callback: (isSuccessful: boolean) => void,
  sampleRate?: string
): void {
  try {
    const fileName = generateFileName(
      voiceName,
      nativeLanguage,
      "json",
      sampleRate
    );

    const textBlob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(textBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    callback(true);
  } catch {
    callback(false);
  }
}

export function updateWavHeader(buffer: Uint8Array) {
  const view = new DataView(buffer.buffer);

  // The file size (minus the "RIFF" and file size fields) is at byte 4, little-endian
  const fileSize = buffer.length - 8;
  view.setUint32(4, fileSize, true);

  // The data chunk size is at byte 40, little-endian
  const dataChunkSize = buffer.length - 44;
  view.setUint32(40, dataChunkSize, true);
}

export async function readableStreamToWavBlob(
  readableStream: ReadableStream<Uint8Array>
): Promise<Blob> {
  // Read the ReadableStream and convert it to an array of Uint8Array
  const reader = readableStream.getReader();
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      totalLength += value.length;
    }
  }

  // Combine all chunks into a single Uint8Array
  const combined = new Uint8Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  // Update the WAV header with the correct file size and data chunk size
  updateWavHeader(combined);

  // Create a Blob from the updated Uint8Array
  const wavBlob = new Blob([combined], { type: "audio/x-wav" });

  return wavBlob;
}

export function fixWavHeaderAndDownload(url: string, fileName: string) {
  fetch(url).then(async (response) => {
    if (response.body) {
      const wavBlob = await readableStreamToWavBlob(response.body);

      // Save the Blob as a file (for example, in the browser)
      const newUrl = URL.createObjectURL(wavBlob);
      const a = document.createElement("a");
      a.href = newUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
    }
  });
}

/**
 * Function to download the given audio url as a WAV file
 * @param url - url to download the WAV from
 * @param voiceName - name of the voice used for the WAV file
 * @param nativeLanguage - native language of the voice used for the WAV file
 * @param callback - callback function to trigger with a success or failure indicator
 * @param customFileName - custom file name for the downloaded file
 * @param sampleRate - optional sample rate parameter
 */
export function downloadAsWAV(
  url: string,
  voiceName: string,
  nativeLanguage: string,
  callback: (isSuccessful: boolean) => void,
  sampleRate?: string,
  customFileName?: string
): void {
  // check if the url is created from a blob
  // in this case the url will be: "blob:http://..."
  try {
    const fileName =
      customFileName ||
      generateFileName(voiceName, nativeLanguage, "wav", sampleRate);

    // Always fix the WAV header and download since the audio might be streamed
    fixWavHeaderAndDownload(url, fileName);
  } catch {
    callback(false);
  }
}

export function getTTSUrl(envName: string) {
  if (runtimeConfig.ttsOnPrem) {
    if (runtimeConfig.ttsEnvoyUrl) return runtimeConfig.ttsEnvoyUrl;
    // eslint-disable-next-line no-console
    console.warn("ttsEnvoyUrl missing; falling back to dev endpoint");
    return "https://dev.tts.allinga-plattform.de";
  }

  const envKey = envName.toLowerCase();
  const envUrl = runtimeConfig.ttsEnvoyServiceUrls[envKey];

  if (envUrl) return envUrl;

  // eslint-disable-next-line no-console
  console.warn(
    `Envoy URL for ${envName} not set in runtimeConfig; falling back to dev endpoint`
  );
  return "https://dev.tts.allinga-plattform.de";
}

export async function fetchTTS(envName: string, token: string): Promise<TTS[]> {
  const baseUrl = getTTSUrl(envName);
  const transport = new GrpcWebFetchTransport({ baseUrl });
  const client = new TextToSpeechClient(transport);
  const rpcOptions: RpcOptions = {
    meta: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const call = client.enumerate(Empty, rpcOptions);
    const response = await call.response;
    return response.flavors;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("TTS enumerate error:", error);
    return [];
  }
}

export async function fetchTTSOnPrem(
  envName: string,
  token: string
): Promise<ListOfVoices_VoicesPerFlavor[]> {
  const baseUrl = getTTSUrl(envName);
  const transport = new GrpcWebFetchTransport({ baseUrl });
  const client = new TextToSpeechClient(transport);
  const rpcOptions: RpcOptions = {
    meta: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const call = client.enumerateVoices(Empty, rpcOptions);
    const response = await call.response;
    return response.flavors;
  } catch (error) {
    return [];
  }
}

export async function getPhoneme(
  token: string,
  text: string,
  language: string,
  voice: string,
  sampleRate: string,
  envName: string,
  selectedDeployment: string
) {
  const baseUrl = getTTSUrl(envName);
  const transport = new GrpcWebFetchTransport({ baseUrl });
  const client = new TextToSpeechClient(transport);
  const request: TextToSpeechRequest = {
    environment: envName,
    flavor: selectedDeployment,
    text,
    language: language,
    voice: voice,
    audioFormat: {
      encoding: AudioFormat_Encoding.WAV,
      samplerate: +sampleRate,
      bitrate: 0,
    },
    speechRate: 1.0,
    debug: false,
  };
  const rpcOptions: RpcOptions = {
    meta: {
      Authorization: `Bearer ${token}`,
    },
  };
  const call = client.synthesize(request, rpcOptions);
  let result = "";
  for await (const response of call.responses) {
    if (response.phoneme) {
      result += response.phoneme;
    }
  }
  return result;
}

/**
 * Get the preview prompt for a voice in the specified language
 */
export function getPreviewPrompt(
  voice: VoiceDescriptionProps,
  language: string = "de-DE"
): string {
  if (!voice.previewPrompt || voice.previewPrompt.length === 0) {
    return `Dies ist die Stimme von ${voice.voiceName}.`;
  }

  // Normalize language code (handle "de-DE", "de-de")
  const normalizedLanguage = language.toLowerCase().replace("_", "-");

  // Find exact match
  const match = voice.previewPrompt.find(
    (p) => p.language.toLowerCase() === normalizedLanguage
  );

  if (match) {
    return match.text;
  }

  // If exact language not found, try language prefix (de-DE -> de)
  const languagePrefix = normalizedLanguage.split("-")[0];
  const prefixMatch = voice.previewPrompt.find((p) =>
    p.language.toLowerCase().startsWith(languagePrefix)
  );

  if (prefixMatch) {
    return prefixMatch.text;
  }

  // This shouldn't happen if all voices have all languages, but just in case
  // eslint-disable-next-line no-console
  console.error(
    `No preview prompt found for ${voice.voiceName} in language ${language}`
  );
  return (
    voice.previewPrompt[0]?.text ||
    `Dies ist die Stimme von ${voice.voiceName}.`
  );
}

/**
 * Dynamically generate language mapping from available TTS data
 * Falls back to static mapping for display names
 */
export function generateDynamicLanguageMapping(
  ttsData: TTS[]
): Record<string, string> {
  const mapping: Record<string, string> = {};

  ttsData.forEach((flavor) => {
    flavor.languages.forEach((lang) => {
      const langCode = lang.name.toLowerCase();
      // Use static mapping if available, otherwise use the code itself
      mapping[langCode] =
        TTSLanguageMapping[langCode as TTSLanguageType] || lang.name;
    });
  });

  return mapping;
}

/**
 * Get all unique languages
 */
export function getAllUniqueLanguages(ttsData: TTS[]): TTSLanguage[] {
  const languageMap = new Map<string, TTSLanguage>();

  ttsData.forEach((flavor) => {
    flavor.languages.forEach((lang) => {
      const existing = languageMap.get(lang.name);
      if (existing) {
        const combinedVoices = new Set([...existing.voices, ...lang.voices]);
        languageMap.set(lang.name, {
          name: lang.name,
          voices: Array.from(combinedVoices),
        });
      } else {
        languageMap.set(lang.name, { ...lang });
      }
    });
  });

  return Array.from(languageMap.values());
}

/**
 * Extract all unique styles from the ListOfVoices response
 * @param listOfVoices
 * @returns
 */
export function extractUniqueStyles(
  listOfVoices: ListOfVoices | undefined
): string[] {
  if (!listOfVoices?.flavors) {
    return [];
  }

  const stylesSet = new Set<string>();

  for (const flavor of listOfVoices.flavors) {
    if (!flavor.voices) continue;

    for (const voice of flavor.voices) {
      const styles = normalizeStyles(voice.style);

      styles.forEach((style) => {
        if (style !== "unknown") {
          stylesSet.add(style);
        }
      });
    }
  }

  return Array.from(stylesSet).sort();
}

/**
 * Generate EmotionProps array from available styles
 * Maps styles to icons where available, uses default icon for unknown styles
 * @param listOfVoices
 * @returns
 */
export function generateDynamicEmotions(
  listOfVoices: ListOfVoices | undefined
): EmotionProps[] {
  const uniqueStyles = extractUniqueStyles(listOfVoices);
  return uniqueStyles.map((styleName) => ({
    icon: getIconForStyle(styleName),
    name: styleName,
  }));
}

/**
 * Get styles available for a specific voice
 * @param voiceName
 * @param listOfVoices
 * @returns
 */
export function getStylesForVoice(
  voiceName: string,
  listOfVoices: ListOfVoices | undefined
): string[] {
  if (!listOfVoices?.flavors) {
    return [];
  }

  for (const flavor of listOfVoices.flavors) {
    const voice = flavor.voices?.find((v) => v.name === voiceName);
    if (voice?.style) {
      return normalizeStyles(voice.style).sort();
    }
  }

  return [];
}

/**
 * Handles array, string (comma-separated), and object formats
 */
function normalizeStyles(styleField: unknown): string[] {
  if (!styleField) return [];

  // Array format
  if (Array.isArray(styleField)) {
    return styleField
      .filter((s): s is string => typeof s === "string" && !!s.trim())
      .map((s) => s.trim());
  }

  // String format (comma-separated)
  if (typeof styleField === "string") {
    return styleField
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Object format (keys are style names)
  if (typeof styleField === "object" && styleField !== null) {
    return Object.keys(styleField)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
}
